import { State, orderBy, process } from '@progress/kendo-data-query';
import { TFunction } from 'i18next';
import { FC, ReactNode, createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { CanManageDepartments } from 'src/components/guards/PermissionsList';
import useBoolean from 'src/hooks/useBoolean';
import useCompany from 'src/hooks/useCompany';
import useConfirm from 'src/hooks/useConfirm';
import usePermission from 'src/hooks/usePermissions';
import usePostMessage from 'src/hooks/usePostMessage';
import useDeleteDepartmentsApi, {
  DeleteDepartmentRequest,
} from 'src/services/useDeleteDepartmentsApi';
import useGetAllGlCodeApi, { GlCodeResponse } from 'src/services/useGetAllGlCode';
import useGetClientApi, { ClientResponse } from 'src/services/useGetClientApi';
import useGetDepartmentApi, {
  Department,
  DepartmentMembers,
} from 'src/services/useGetDepartmentApi';
import useUpdateDepartmentApi from 'src/services/useUpdateDepartmentApi';
import { Company } from 'src/types/Companies';
import { SetState } from 'src/types/generic';

export const initialDepartmentState: State = {
  skip: 0,
  take: 350,
  sort: [{ field: 'name', dir: 'asc' }],
  filter: {
    logic: 'and',
    filters: [],
  },
};

interface ViewDepartmentContextType {
  departments: Department[];
  setDepartments: SetState<Department[]>;
  departmentList: SelectedDepartment[];

  selectedDepartment: SelectedDepartment;
  setSelectedDepartment: SetState<SelectedDepartment>;

  departmentsDataState: State;
  setDepartmentsDataState: SetState<State>;

  clients: ClientResponse[];
  glCodes: GlCodeResponse[];

  assignedGlCodes: GlCodeResponse[];
  setAssignedGlCodes: SetState<GlCodeResponse[]>;
  unassignedGlCodes: GlCodeResponse[];
  setUnassignedGlCodes: SetState<GlCodeResponse[]>;

  assignedMembers: DepartmentMembers[];
  setAssignedMembers: SetState<DepartmentMembers[]>;
  unassignedMembers: DepartmentMembers[];
  setUnassignedMembers: SetState<DepartmentMembers[]>;

  selectedCompany: Company;
  hasEditDepartmentPermission: boolean;
  isDepartmentLoading: boolean;
  isManageDepartmentDirty: boolean;
  refreshTable: (keepSelection?: boolean) => Promise<void>;

  isEditMode: boolean;
  setIsEditMode: SetState<boolean>;

  departmentTableData: Department[];
  setDepartmentTableData: SetState<Department[]>;
  departmentTotalWithFilters: number;

  excelLoading: boolean;
  setExcelLoading: SetState<boolean>;

  t: TFunction<'translation', undefined>;
  pageTitle: string;
  buId: string;
  handleSaveClick: VoidFunction;
  companyId: string;
  isSaveDepartmentLoading: boolean;
  unDeletedDepartmentList: Department[];

  selectedRows: Department[];
  setSelectedRows: SetState<Department[]>;

  deleteDepartment: (params?: DeleteDepartmentRequest | undefined) => Promise<any>;
  isDepartmentDeleting: boolean;

  bulkAssignRefresher: boolean;
  confirmBeforeProceeding: (acceptCallback: VoidFunction) => void;
  refreshBulkAssign: VoidFunction;
}

export const ViewDepartmentContext = createContext<ViewDepartmentContextType>(
  {} as ViewDepartmentContextType
);

interface ViewDepartmentContextProps {
  children: ReactNode;
}
export interface SelectedDepartment {
  departmentId: number;
  departmentName: string;
}

const ViewDepartmentContextProvider: FC<ViewDepartmentContextProps> = ({ children }) => {
  const { confirm } = useConfirm();
  const { company: selectedCompany } = useCompany();
  const { t } = useTranslation();
  const { companyId } = selectedCompany;
  const [selectedRows, setSelectedRows] = useState<Department[]>([]);

  const hasEditDepartmentPermission = usePermission({ permissions: CanManageDepartments });
  const { departments, setDepartments, getDepartments, isDepartmentLoading, buId } =
    useGetDepartmentApi(companyId);
  const { clients, getClients } = useGetClientApi(companyId);
  const { glCodes } = useGetAllGlCodeApi(companyId);
  const { updateDepartments } = useUpdateDepartmentApi();
  const { isLoading: isDepartmentDeleting, deleteDepartment } = useDeleteDepartmentsApi(companyId);

  const [departmentsDataState, setDepartmentsDataState] = useState<State>({
    ...initialDepartmentState,
  });

  const { value: isEditMode, setBool: setIsEditMode } = useBoolean(false);
  const [selectedDepartment, setSelectedDepartment] = useState<SelectedDepartment>(
    {} as SelectedDepartment
  );
  const [excelLoading, setExcelLoading] = useState(false);
  const [departmentTableData, setDepartmentTableData] = useState<Department[]>([]);
  const { value: bulkAssignRefresher, toggle: refreshBulkAssign } = useBoolean();

  const departmentList: SelectedDepartment[] = useMemo(
    () =>
      orderBy(
        [
          ...departments
            .filter((department) => !department.isDeleted)
            .map((item) => ({
              departmentId: item.departmentId,
              departmentName: item.name,
            })),
        ],
        [{ field: 'departmentName', dir: 'asc' }]
      ),
    [departments]
  );

  const unDeletedDepartmentList = useMemo(
    () =>
      orderBy(
        [...departments.filter((department) => !department.isDeleted)],
        [{ field: 'name', dir: 'asc' }]
      ),
    [departments]
  );

  useEffect(() => {
    if (selectedDepartment?.departmentId && departments.length) {
      const assignedGlCode =
        departments.find(
          (department) => department.departmentId === selectedDepartment?.departmentId
        )?.glCodes ?? ([] as GlCodeResponse[]);
      const initialAssignedGlCodes = orderBy(assignedGlCode, [{ field: 'name', dir: 'asc' }]);

      const assignedGlCodeList = initialAssignedGlCodes.map((glCode) => glCode.id);
      const unassignedGlCode = glCodes.filter((glCode) => !assignedGlCodeList.includes(glCode.id));
      const initialUnassignedGlCodes = orderBy(unassignedGlCode, [{ field: 'name', dir: 'asc' }]);

      setAssignedGlCodes(initialAssignedGlCodes);
      setUnassignedGlCodes(initialUnassignedGlCodes);
    }
  }, [departments, glCodes, selectedDepartment?.departmentId]);

  useEffect(() => {
    if (selectedDepartment?.departmentId && departments.length) {
      const assignedMember =
        departments.find(
          (department) => department.departmentId === selectedDepartment?.departmentId
        )?.members ?? ([] as DepartmentMembers[]);
      const initialAssignedMembers = orderBy(assignedMember, [{ field: 'name', dir: 'asc' }]);

      const assignedMemberList = initialAssignedMembers.map((member) => member.id);
      const unassignedMember = clients
        .filter((member) => !assignedMemberList.includes(member.clientId))
        ?.map((member) => {
          const { name, clientId, isDeleted, rowId } = member;
          return {
            name,
            id: clientId,
            isDeleted,
            rowId,
            selected: false,
            isDirty: false,
          };
        });
      const initialUnassignedMembers: DepartmentMembers[] = orderBy(unassignedMember, [
        { field: 'name', dir: 'asc' },
      ]);

      setAssignedMembers(initialAssignedMembers);
      setUnassignedMembers(initialUnassignedMembers);
    }
  }, [clients, departments, selectedDepartment?.departmentId]);

  const [unassignedGlCodes, setUnassignedGlCodes] = useState<GlCodeResponse[]>([]);
  const [assignedGlCodes, setAssignedGlCodes] = useState<GlCodeResponse[]>([]);
  const [unassignedMembers, setUnassignedMembers] = useState<DepartmentMembers[]>([]);
  const [assignedMembers, setAssignedMembers] = useState<DepartmentMembers[]>([]);
  const [isSaveDepartmentLoading, setIsSaveDepartmentLoading] = useState(false);

  const isGLCodeDirty = useMemo(
    () =>
      assignedGlCodes.some((glCode) => glCode.isDirty) ||
      unassignedGlCodes.some((glCode) => glCode.isDirty),
    [assignedGlCodes, unassignedGlCodes]
  );

  const isMemberDirty = useMemo(
    () =>
      assignedMembers.some((member) => member.isDirty) ||
      unassignedMembers.some((member) => member.isDirty),
    [assignedMembers, unassignedMembers]
  );

  const isManageDepartmentDirty = useMemo(
    () => isGLCodeDirty || isMemberDirty,
    [isGLCodeDirty, isMemberDirty]
  );

  const refreshTable = useCallback(
    async (keepSelection = false) => {
      const response = await getDepartments();
      const { departments } = response;
      await getClients();

      if (!keepSelection) {
        setSelectedRows([]);
      } else {
        setSelectedRows((prev) => {
          const prevSelectedRowIds = prev.map((dept) => dept.departmentId);
          return departments
            .filter((dept) => prevSelectedRowIds.includes(dept.departmentId))
            .map((dept) => ({ ...dept, selected: true }));
        });
      }
    },
    [getClients, getDepartments]
  );

  const handleSaveClick = useCallback(() => {
    setIsSaveDepartmentLoading(true);
    const assignedGlCodeIds = assignedGlCodes.map((glCode) => glCode.id);
    const assignedMemberIds = assignedMembers.map((member) => member.id);

    updateDepartments({
      companyId,
      departmentId: selectedDepartment.departmentId,
      name: selectedDepartment.departmentName,
      glCodes: assignedGlCodeIds,
      members: assignedMemberIds,
    })
      .then(async () => {
        toast.success(t('Department assignments updated successfully'));
        refreshBulkAssign();
        await refreshTable();
      })
      .finally(() => {
        setIsSaveDepartmentLoading(false);
      });
  }, [
    assignedGlCodes,
    assignedMembers,
    companyId,
    refreshBulkAssign,
    refreshTable,
    selectedDepartment.departmentId,
    selectedDepartment.departmentName,
    t,
    updateDepartments,
  ]);

  const { data: departmentWithFilters, total: departmentTotalWithFilters } = useMemo(() => {
    const selectedIds = selectedRows.map((row) => row.departmentId);
    const departmentsWithSelect = unDeletedDepartmentList.map((row) => ({
      ...row,
      selected: selectedIds.includes(row.departmentId),
    }));
    return process(departmentsWithSelect, departmentsDataState) as {
      data: Department[];
      total: number;
    };
  }, [departmentsDataState, selectedRows, unDeletedDepartmentList]);

  useEffect(() => {
    setDepartmentTableData(departmentWithFilters);
  }, [departmentWithFilters, departments]);

  useEffect(() => {
    if (companyId) {
      refreshTable();
    }
  }, [companyId, refreshTable]);

  const pageTitle = useMemo(
    () => (hasEditDepartmentPermission ? t('View/Edit Departments') : t('View Departments')),
    [hasEditDepartmentPermission, t]
  );

  const dirtyMessage = useMemo(
    () => ({ isDirty: isManageDepartmentDirty }),
    [isManageDepartmentDirty]
  );
  usePostMessage(dirtyMessage);

  const confirmBeforeProceeding = useCallback(
    (acceptCallback: VoidFunction) => {
      if (isManageDepartmentDirty) {
        confirm({
          title: 'Unsaved Changes',
          content:
            'Unsaved changes will be lost by proceeding. Cancel to return and save your changes.',
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Discard and Proceed',
          width: '725px',
        }).then((isAccepted) => {
          if (isAccepted) {
            acceptCallback();
          }
        });
      } else {
        acceptCallback();
      }
    },
    [confirm, isManageDepartmentDirty]
  );

  return (
    <ViewDepartmentContext.Provider
      value={{
        unDeletedDepartmentList,
        departments,
        setDepartments,
        selectedCompany,
        departmentList,
        selectedDepartment,
        setSelectedDepartment,
        isDepartmentLoading,
        departmentsDataState,
        setDepartmentsDataState,
        hasEditDepartmentPermission,
        clients,
        glCodes,
        isManageDepartmentDirty,
        assignedGlCodes,
        setAssignedGlCodes,
        unassignedGlCodes,
        setUnassignedGlCodes,
        assignedMembers,
        setAssignedMembers,
        unassignedMembers,
        setUnassignedMembers,
        refreshTable,
        isEditMode,
        setIsEditMode,
        departmentTotalWithFilters,
        excelLoading,
        setExcelLoading,
        t,
        pageTitle,
        buId,
        handleSaveClick,
        companyId,
        isSaveDepartmentLoading,
        departmentTableData,
        setDepartmentTableData,
        selectedRows,
        setSelectedRows,
        isDepartmentDeleting,
        deleteDepartment,
        bulkAssignRefresher,
        confirmBeforeProceeding,
        refreshBulkAssign,
      }}
    >
      {children}
    </ViewDepartmentContext.Provider>
  );
};

export default ViewDepartmentContextProvider;
